import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiBlob, saveBlob } from '../../api/http';
import {
  Notice,
  SchoolSelect,
  errorMessage,
  primaryButton,
  secondaryButton,
  selectClass,
} from '../../components/common';
import { TextField } from '../../components/TextField';
import { t } from '../../i18n';
import { formatMinor } from '../../lib/money';

type Input = 'academicYearId' | 'classId' | 'dateRange' | 'groupBy';
interface Catalogue {
  canExport: boolean;
  reports: { name: string; title: string; description: string; inputs: Input[] }[];
}
interface Column {
  key: string;
  label: string;
  type: 'text' | 'number' | 'money' | 'percent';
}
type Row = Record<string, string | number | null>;
interface Result {
  title: string;
  currencyCode: string;
  params: Record<string, string | null>;
  columns: Column[];
  rows: Row[];
  totals: Row | null;
  generatedAt: string;
}

const cellText = (column: Column, value: string | number | null, currency: string) => {
  if (value === null || value === undefined) return '';
  if (column.type === 'money') return formatMinor(Number(value), currency);
  if (column.type === 'percent') return `${value}%`;
  return String(value);
};

/** Pick a report, set what it asks for, read it on screen or download it as CSV. */
export function ReportsPage() {
  const [schoolId, setSchoolId] = useState('');
  const [name, setName] = useState('');
  const [classId, setClassId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [groupBy, setGroupBy] = useState('day');
  const [ran, setRan] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const catalogue = useQuery({
    queryKey: ['reports', schoolId, 'catalogue'],
    queryFn: () => api<Catalogue>(`/schools/${schoolId}/reports`),
    enabled: Boolean(schoolId),
  });
  const chosen = catalogue.data?.reports.find((r) => r.name === name) ?? catalogue.data?.reports[0];
  const classes = useQuery({
    queryKey: ['reports', schoolId, 'classes'],
    queryFn: () => api<{ id: string; name: string }[]>(`/schools/${schoolId}/classes`),
    enabled: Boolean(schoolId) && Boolean(chosen?.inputs.includes('classId')),
  });

  const query = (format: 'json' | 'csv') => ({
    format,
    classId: chosen?.inputs.includes('classId') ? classId || undefined : undefined,
    from: chosen?.inputs.includes('dateRange') ? from || undefined : undefined,
    to: chosen?.inputs.includes('dateRange') ? to || undefined : undefined,
    groupBy: chosen?.inputs.includes('groupBy') ? groupBy : undefined,
  });
  const key = ['reports', schoolId, chosen?.name, ran];
  const result = useQuery({
    queryKey: key,
    queryFn: () =>
      api<Result>(`/schools/${schoolId}/reports/${chosen!.name}`, { query: query('json') }),
    enabled: Boolean(schoolId && chosen && ran),
  });

  const download = async () => {
    try {
      setError(null);
      saveBlob(
        await apiBlob(`/schools/${schoolId}/reports/${chosen!.name}`, { query: query('csv') }),
        `report-${chosen!.name}.csv`,
      );
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  const data = result.data;
  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <h1 className="text-2xl font-bold">{t('rep.title')}</h1>
      <SchoolSelect
        value={schoolId}
        onChange={(id) => {
          setSchoolId(id);
          setRan(null);
        }}
      />
      {catalogue.data && catalogue.data.reports.length === 0 && (
        <p className="text-slate-600">{t('rep.none')}</p>
      )}
      {catalogue.data && chosen && (
        <form
          aria-label={t('rep.title')}
          className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-4"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            setRan(String(Date.now()));
          }}
        >
          <div className="sm:col-span-2">
            <label htmlFor="rep-name" className="block text-sm font-medium">
              {t('rep.pick')}
            </label>
            <select
              id="rep-name"
              className={selectClass}
              value={chosen.name}
              onChange={(e) => {
                setName(e.target.value);
                setRan(null);
              }}
            >
              {catalogue.data.reports.map((r) => (
                <option key={r.name} value={r.name}>
                  {r.title}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-600">{chosen.description}</p>
          </div>
          {chosen.inputs.includes('classId') && (
            <div>
              <label htmlFor="rep-class" className="block text-sm font-medium">
                {t('rep.class')}
              </label>
              <select
                id="rep-class"
                className={selectClass}
                value={classId}
                onChange={(e) => setClassId(e.target.value)}
              >
                <option value="">{t('rep.allClasses')}</option>
                {classes.data?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {chosen.inputs.includes('dateRange') && (
            <>
              <TextField
                label={t('rep.from')}
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
              <TextField
                label={t('rep.to')}
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </>
          )}
          {chosen.inputs.includes('groupBy') && (
            <div>
              <label htmlFor="rep-group" className="block text-sm font-medium">
                {t('rep.groupBy')}
              </label>
              <select
                id="rep-group"
                className={selectClass}
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
              >
                <option value="day">{t('rep.byDay')}</option>
                <option value="month">{t('rep.byMonth')}</option>
              </select>
            </div>
          )}
          <div className="flex flex-wrap items-end gap-2 sm:col-span-4">
            <button type="submit" className={primaryButton}>
              {t('rep.run')}
            </button>
            {catalogue.data.canExport && (
              <button type="button" className={secondaryButton} onClick={() => void download()}>
                {t('rep.csv')}
              </button>
            )}
          </div>
        </form>
      )}
      {error && <Notice kind="error">{error}</Notice>}
      {result.isError && <Notice kind="error">{errorMessage(result.error)}</Notice>}
      {data && (
        <section aria-label={data.title} className="space-y-2">
          <h2 className="text-lg font-semibold">{data.title}</h2>
          <p className="text-sm text-slate-600">
            {[
              data.params.academicYear
                ? t('rep.academicYear', { name: data.params.academicYear })
                : null,
              data.params.from
                ? t('rep.range', { from: data.params.from, to: data.params.to ?? '' })
                : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
          {data.rows.length === 0 ? (
            <p className="text-slate-600">{t('rep.empty')}</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                  <tr>
                    {data.columns.map((c) => (
                      <th
                        key={c.key}
                        scope="col"
                        className={`p-2 ${c.type === 'text' ? '' : 'text-right'}`}
                      >
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.rows.map((row, i) => (
                    <tr key={i}>
                      {data.columns.map((c) => (
                        <td
                          key={c.key}
                          className={`p-2 ${c.type === 'text' ? '' : 'text-right tabular-nums'}`}
                        >
                          {cellText(c, row[c.key] ?? null, data.currencyCode)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                {data.totals && (
                  <tfoot className="border-t-2 border-slate-300 font-semibold">
                    <tr>
                      {data.columns.map((c) => (
                        <td
                          key={c.key}
                          className={`p-2 ${c.type === 'text' ? '' : 'text-right tabular-nums'}`}
                        >
                          {cellText(c, data.totals![c.key] ?? null, data.currencyCode)}
                        </td>
                      ))}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
          <p className="text-xs text-slate-500">
            {t('rep.generated', { time: new Date(data.generatedAt).toLocaleString() })}
          </p>
        </section>
      )}
    </div>
  );
}
